import type { StoreTicket, StoreTicketMessage } from "@khazzin/api-client";
import type { Translate } from "@khazzin/i18n";
import { Paperclip, X } from "lucide-react";
import { useId, useState } from "react";
import { Icon } from "../icons/Icon";
import { Button } from "../primitives/Button";
import { Textarea } from "../primitives/Input";
import { cx, type Tone } from "../styles";
import { type UploadApi, useUpload } from "./ImportDialog/useUpload";
import { StatusChip } from "./StatusChip";

/**
 * A store ticket's pieces both sides draw (EP10-S12, DA-10-11): the status
 * chip, the SLA's overdue test, the thread of bubbles — the reader's own side
 * at the inline end — and the reply box with its files. The Store Portal and
 * the console's Store tickets desk each fetch from their own door and hand
 * the answer here.
 */

/** The purpose a ticket's files are uploaded under, owned by the store. */
export const TICKET_FILE_PURPOSE = "store_ticket_attachment";

/** At most this many files on one message. */
export const TICKET_FILE_LIMIT = 5;

export const TICKET_STATUS_TONES: Readonly<Record<string, Tone>> = {
	open: "info",
	in_progress: "info",
	waiting_on_store: "warning",
	resolved: "success",
	closed: "neutral",
};

export function TicketStatusChip({ status, t }: { status: string; t: Translate }) {
	return (
		<StatusChip tone={TICKET_STATUS_TONES[status] ?? "neutral"} size="sm">
			{t(`tickets.status.${status}`)}
		</StatusChip>
	);
}

/** Past its SLA and still with somebody to answer it. */
export function slaOverdue(ticket: Pick<StoreTicket, "bucket" | "slaDueAt">, now = Date.now()) {
	return ticket.bucket !== "resolved" && Date.parse(ticket.slaDueAt) < now;
}

export interface TicketThreadProps {
	messages: readonly StoreTicketMessage[];
	/** Whose bubbles sit at the inline end: the reader's side. */
	mine: "store" | "operator";
	t: Translate;
	formatTime: (iso: string) => string;
}

export function TicketThread({ messages, mine, t, formatTime }: TicketThreadProps) {
	return (
		<ol aria-label={t("tickets.thread.label")} className="flex flex-col gap-3">
			{messages.map((m) => {
				const own = m.side === mine;
				const system = m.side === "system";
				return (
					<li
						key={m.id}
						className={cx(
							"flex max-w-[85%] flex-col gap-1 rounded-md border px-3 py-2 text-[13px]",
							system
								? "self-center border-border bg-bg-subtle text-fg-muted"
								: own
									? "self-end border-brand bg-brand-subtle text-brand-ink"
									: "self-start border-border bg-bg-surface text-fg",
						)}
					>
						<span className="text-[11.5px] font-semibold">
							{t(`tickets.side.${m.side}`)} · {formatTime(m.createdAt)}
						</span>
						<p className="whitespace-pre-wrap break-words">{m.body}</p>
						{m.attachments.length > 0 && (
							<ul className="flex flex-wrap gap-2">
								{m.attachments.map((a) => (
									<li key={a.id}>
										{a.url ? (
											<a
												href={a.url}
												target="_blank"
												rel="noreferrer"
												className="inline-flex items-center gap-1 underline"
											>
												<Icon as={Paperclip} size={14} />
												{a.filename}
											</a>
										) : (
											<span className="inline-flex items-center gap-1">
												<Icon as={Paperclip} size={14} />
												{a.filename}
											</span>
										)}
									</li>
								))}
							</ul>
						)}
					</li>
				);
			})}
		</ol>
	);
}

export interface TicketFile {
	id: string;
	filename: string;
}

export interface TicketFilesProps {
	api: UploadApi;
	/** The store the files belong to. */
	storeId: string;
	files: readonly TicketFile[];
	onChange: (files: TicketFile[]) => void;
	t: Translate;
	disabled?: boolean;
}

/** Photos and PDFs for a ticket or a reply, uploaded as they are picked. */
export function TicketFiles({ api, storeId, files, onChange, t, disabled }: TicketFilesProps) {
	const inputId = useId();
	const { state, upload } = useUpload({ api, purpose: TICKET_FILE_PURPOSE, ownerId: storeId });
	const busy = state.status === "uploading";
	return (
		<div className="flex flex-col gap-2">
			<div className="flex flex-wrap items-center gap-2">
				<label
					htmlFor={inputId}
					className={cx(
						"inline-flex min-h-10 cursor-pointer items-center gap-1 rounded-sm border border-border px-3 text-[13px]",
						busy || disabled || files.length >= TICKET_FILE_LIMIT
							? "pointer-events-none bg-bg-subtle text-fg-muted"
							: "text-fg",
					)}
				>
					<Icon as={Paperclip} size={16} />
					{busy ? t("tickets.files.uploading") : t("tickets.files.add")}
				</label>
				<input
					id={inputId}
					type="file"
					accept="image/jpeg,image/png,image/webp,application/pdf"
					className="sr-only"
					disabled={busy || disabled || files.length >= TICKET_FILE_LIMIT}
					onChange={(event) => {
						const file = event.target.files?.[0];
						event.target.value = "";
						if (file === undefined) {
							return;
						}
						void upload(file).then(
							(a) => onChange([...files, { id: a.id, filename: a.filename }]),
							() => undefined,
						);
					}}
				/>
				<span className="text-[12px] text-fg-muted">
					{t("tickets.files.hint", { limit: TICKET_FILE_LIMIT })}
				</span>
			</div>
			{state.status === "error" && (
				<p role="alert" className="text-[12px] text-danger-fg">
					{t("tickets.files.failed")}
				</p>
			)}
			{files.length > 0 && (
				<ul className="flex flex-wrap gap-2">
					{files.map((f) => (
						<li
							key={f.id}
							className="inline-flex items-center gap-1 rounded-sm bg-bg-subtle px-2 py-1 text-[12px] text-fg"
						>
							{f.filename}
							<button
								type="button"
								aria-label={t("tickets.files.remove", { name: f.filename })}
								onClick={() => onChange(files.filter((x) => x.id !== f.id))}
								className="grid size-6 place-items-center rounded-sm hover:bg-bg-surface"
							>
								<Icon as={X} size={12} />
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

export interface TicketComposerProps {
	t: Translate;
	/** Sends the reply; a refusal is shown under the box and the text kept. */
	onSend: (body: string, attachmentIds: string[]) => Promise<unknown>;
	/** Files, when the reader may attach them. */
	files?: { api: UploadApi; storeId: string };
	disabled?: boolean;
	/** More controls beside Send — the desk's status select. */
	extra?: React.ReactNode;
}

export function TicketComposer({ t, onSend, files, disabled, extra }: TicketComposerProps) {
	const [body, setBody] = useState("");
	const [attached, setAttached] = useState<TicketFile[]>([]);
	const [sending, setSending] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const empty = body.trim() === "";
	return (
		<form
			aria-label={t("tickets.reply.label")}
			className="flex flex-col gap-2"
			onSubmit={(event) => {
				event.preventDefault();
				if (empty || sending) {
					return;
				}
				setSending(true);
				setError(null);
				onSend(
					body.trim(),
					attached.map((f) => f.id),
				)
					.then(
						() => {
							setBody("");
							setAttached([]);
						},
						(cause: unknown) =>
							setError(cause instanceof Error ? cause.message : t("tickets.reply.failed")),
					)
					.finally(() => setSending(false));
			}}
		>
			<Textarea
				aria-label={t("tickets.reply.label")}
				placeholder={t("tickets.reply.placeholder")}
				value={body}
				rows={3}
				maxLength={4000}
				disabled={disabled}
				onChange={(event) => setBody(event.target.value)}
			/>
			{files !== undefined && (
				<TicketFiles
					api={files.api}
					storeId={files.storeId}
					files={attached}
					onChange={setAttached}
					t={t}
					disabled={disabled === true}
				/>
			)}
			{error !== null && (
				<p role="alert" className="text-[12px] text-danger-fg">
					{error}
				</p>
			)}
			<div className="flex flex-wrap items-center justify-end gap-2">
				{extra}
				<Button type="submit" variant="primary" disabled={empty || sending || disabled === true}>
					{t("tickets.reply.send")}
				</Button>
			</div>
		</form>
	);
}
